import jsPDF from 'jspdf';
import { format } from 'date-fns';
import {
  formatDate,
  formatCurrency,
  getServiceCallListCityState,
  getServiceCallListCustomerName,
  getStatusText,
  truncate
} from '@shared/utils';

/** @typedef {{ id: string, label: string, defaultSelected?: boolean }} ListColumnMeta */

/** @type {ListColumnMeta[]} */
export const SERVICE_CALL_LIST_COLUMNS = [
  { id: 'clientName', label: 'Client', defaultSelected: true },
  { id: 'cityState', label: 'City, State', defaultSelected: true },
  { id: 'scheduledDate', label: 'Scheduled', defaultSelected: true },
  { id: 'completedDate', label: 'Completed', defaultSelected: true },
  { id: 'technician', label: 'Technician', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'priority', label: 'Priority', defaultSelected: false },
  { id: 'type', label: 'Type', defaultSelected: true },
  { id: 'invoiceNumber', label: 'Invoice #', defaultSelected: true },
  { id: 'totalCost', label: 'Total', defaultSelected: true },
  { id: 'workSummary', label: 'Work summary', defaultSelected: true },
  { id: 'customerNotes', label: 'Customer notes', defaultSelected: false },
  { id: 'internalNotes', label: 'Internal notes', defaultSelected: false },
  { id: 'mileage', label: 'Mileage', defaultSelected: false },
  { id: 'generatorHours', label: 'Gen. hours', defaultSelected: false },
  { id: 'warranty', label: 'Warranty', defaultSelected: false }
];

export const DEFAULT_EXPORT_COLUMN_IDS = SERVICE_CALL_LIST_COLUMNS
  .filter((c) => c.defaultSelected)
  .map((c) => c.id);

const statusDisplay = (status) => {
  if (!status) return '';
  if (status === 'assigned') return 'Assigned';
  return getStatusText(status);
};

const priorityDisplay = (p) => {
  if (!p) return '';
  return getStatusText(p);
};

/**
 * @param {object} serviceCall
 * @param {string} columnId
 * @returns {string}
 */
export function formatServiceCallListCell(serviceCall, columnId) {
  const tech = serviceCall?.technician;

  switch (columnId) {
    case 'clientName':
      return getServiceCallListCustomerName(serviceCall);
    case 'cityState':
      return getServiceCallListCityState(serviceCall);
    case 'scheduledDate':
      return formatDate(serviceCall.scheduledDate, 'MM/dd/yyyy');
    case 'completedDate':
      return formatDate(serviceCall.completedDate, 'MM/dd/yyyy');
    case 'technician':
      return tech ? `${tech.firstName || ''} ${tech.lastName || ''}`.trim() : '';
    case 'status':
      return statusDisplay(serviceCall.status);
    case 'priority':
      return priorityDisplay(serviceCall.priority);
    case 'type':
      return serviceCall.type?.name || '';
    case 'invoiceNumber':
      return serviceCall.invoiceNumber || '';
    case 'totalCost':
      return serviceCall.totalCost != null && serviceCall.totalCost !== ''
        ? formatCurrency(Number(serviceCall.totalCost))
        : '';
    case 'workSummary':
      return truncate(serviceCall.workPerformed || serviceCall.description || '', 220);
    case 'customerNotes':
      return truncate(serviceCall.customerNotes || '', 220);
    case 'internalNotes':
      return truncate(serviceCall.internalNotes || '', 220);
    case 'mileage':
      return serviceCall.mileage != null && serviceCall.mileage !== ''
        ? String(serviceCall.mileage)
        : '';
    case 'generatorHours':
      return serviceCall.generatorHours != null && serviceCall.generatorHours !== ''
        ? String(serviceCall.generatorHours)
        : '';
    case 'warranty':
      return serviceCall.isWarranty ? 'Yes' : 'No';
    default:
      return '';
  }
}

/**
 * @param {object[]} serviceCalls
 * @param {string[]} columnIds
 * @returns {string[][]}
 */
export function buildListTableRows(serviceCalls, columnIds) {
  return serviceCalls.map((sc) =>
    columnIds.map((id) => formatServiceCallListCell(sc, id))
  );
}

/**
 * @param {import('jspdf').jsPDF} doc
 * @param {{ width: number }[]} colMetas
 * @param {string[]} cellStrings
 * @param {number} lineHeight
 * @param {boolean} header
 * @returns {{ wrapped: string[][], rowH: number }}
 */
function getRowMetrics(doc, colMetas, cellStrings, lineHeight, header) {
  doc.setFont('helvetica', header ? 'bold' : 'normal');
  doc.setFontSize(header ? 7.5 : 7);

  const maxLinesPerCell = 22;
  const wrapped = cellStrings.map((text, i) => {
    const lines = doc.splitTextToSize(text || '—', Math.max(4, colMetas[i].width - 2));
    if (lines.length > maxLinesPerCell) {
      return [...lines.slice(0, maxLinesPerCell - 1), '…'];
    }
    return lines;
  });
  const maxLines = Math.max(1, ...wrapped.map((lines) => lines.length));
  const rowH = maxLines * lineHeight + 2;
  return { wrapped, rowH };
}

/**
 * @param {import('jspdf').jsPDF} doc
 * @param {{ width: number }[]} colMetas
 * @param {string[][]} wrapped
 * @param {number} y
 * @param {number} left
 * @param {number} lineHeight
 */
function drawWrappedRow(doc, colMetas, wrapped, y, left, lineHeight) {
  let x = left;
  wrapped.forEach((lines, i) => {
    let lineY = y + lineHeight;
    lines.forEach((line) => {
      doc.text(line, x + 1, lineY);
      lineY += lineHeight;
    });
    x += colMetas[i].width;
  });
}

/**
 * @param {object[]} serviceCalls
 * @param {{
 *   columnIds?: string[],
 *   title?: string,
 *   filterSummaryLines?: string[],
 *   generatedAt?: Date
 * }} [options]
 * @returns {import('jspdf').jsPDF}
 */
export function buildServiceCallListPdf(serviceCalls, options = {}) {
  const columnIds = options.columnIds?.length
    ? options.columnIds
    : DEFAULT_EXPORT_COLUMN_IDS;
  const title = options.title || 'Service calls';
  const filterLines = options.filterSummaryLines || [];
  const generatedAt = options.generatedAt || new Date();

  const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const left = margin;
  const right = pageW - margin;
  const tableWidth = right - left;
  const lineHeight = 3.2;

  const labels = columnIds.map(
    (id) => SERVICE_CALL_LIST_COLUMNS.find((c) => c.id === id)?.label || id
  );
  const colWidth = tableWidth / columnIds.length;
  const colMetas = columnIds.map(() => ({ width: colWidth, label: '' }));

  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, left, y);
  y += 7;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${format(generatedAt, 'MMM d, yyyy h:mm a')}`, left, y);
  y += 5;

  if (filterLines.length > 0) {
    doc.setFontSize(7.5);
    filterLines.forEach((line) => {
      doc.text(line, left, y);
      y += 3.5;
    });
    y += 2;
  }

  doc.setFontSize(8);
  doc.text(`Rows: ${serviceCalls.length}`, left, y);
  y += 6;

  const headerLabels = labels;
  const rows = buildListTableRows(serviceCalls, columnIds);

  const drawHeaderBand = () => {
    const { wrapped, rowH } = getRowMetrics(doc, colMetas, headerLabels, lineHeight, true);
    doc.setDrawColor(200);
    doc.setLineWidth(0.2);
    drawWrappedRow(doc, colMetas, wrapped, y, left, lineHeight);
    doc.line(left, y + rowH - 1, right, y + rowH - 1);
    y += rowH + 1;
  };

  drawHeaderBand();

  rows.forEach((cells) => {
    const { wrapped, rowH } = getRowMetrics(doc, colMetas, cells, lineHeight, false);
    if (y + rowH > pageH - margin) {
      doc.addPage();
      y = margin;
      drawHeaderBand();
    }
    drawWrappedRow(doc, colMetas, wrapped, y, left, lineHeight);
    y += rowH;
  });

  return doc;
}

/**
 * @param {object[]} serviceCalls
 * @param {{ columnIds?: string[], title?: string, filterSummaryLines?: string[], filename?: string }} [options]
 */
export function downloadServiceCallListPdf(serviceCalls, options = {}) {
  const doc = buildServiceCallListPdf(serviceCalls, options);
  const filename =
    options.filename ||
    `service_calls_${format(new Date(), 'yyyy-MM-dd_HHmm')}.pdf`;
  doc.save(filename);
}

/**
 * @param {object[]} serviceCalls
 * @param {object} [options]
 */
export function printServiceCallListPdf(serviceCalls, options = {}) {
  const doc = buildServiceCallListPdf(serviceCalls, options);
  doc.autoPrint();
  doc.output('dataurlnewwindow');
}
